import { sharedStyles } from '../../styles/shared-styles';
import { css, html, LitEntity, SirenActionMixin } from '../../utility/lit-entity';
import '../../common/bn-action';
import '../../common/bn-load-more-button';
import '../../common/bn-loading';
import '../../common/bn-flash';
import '../../common/bn-surface';
import '../../organizations/parts/organization-ancestors-bread-crumbs';
import '../../organizations/parts/organization-bread-crumbs';
import '../../organizations/parts/organization-dropdown';

class AssignInstaller extends SirenActionMixin(LitEntity) {
  constructor() {
    super();
    this.query = '';
    this.searchResultOrganizations = [];
    this.childOrganizations = [];
  }

  static get properties() {
    return {
      childOrganizations: { type: Array, attribute: false },
      isFetching: { type: Boolean, attribute: false },
      query: { type: String, attribute: false },
      scheduledFor: { type: String },
      searchResultOrganizations: { type: Array, attribute: false },
      selectedOrganization: { type: Object, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      .actions {
        margin-top: 1em;
        padding: 10px;
        display: flex;
        align-items: center;
        font-size: 1.1em;
      }
      .actions .action-text {
        padding-left: 1em;
      }
      .actions .action-text .destination-name {
        font-weight: bold;
      }
      .main-container {
        display: flex;
        flex-direction: column;
        height: 100%;
      }
      ul {
        list-style: none;
        margin: 0;
        padding: 0;
      }
      .search-container {
          margin-left: auto;
          margin-right: 0.5em;
          width: 70%;
      }
      organization-ancestors-bread-crumbs, organization-bread-crumbs {
        display: inline-block;
        flex-grow: 1;
        --crumb-max-width: 100%;
        --last-crumb-overflow: visible;
        --last-crumb-font-size: 1.1em;
        --last-crumb-font-weight: bold;
      }
      .destination-container {
        margin-top: 20px;
      }
      .destination-header {
        display: flex;
        align-items: baseline;
      }
      .search-results {
        overflow-y: auto;
        margin-top: 0.5em;
      }
      .search-result-item {
        display: flex;
        align-items: center;
        cursor: pointer;
      }
      .search-result-item:hover {
        background-color: var(--hover-color);
      }
      input[type="search"] {
        text-indent: 25px;
        border-radius: 4px;
        background-color: var(--white-base);
      }
      .search-icon {
        position: absolute;
        transform: translate(5px,7px);
      }
      .empty-search-results {
        padding-left: 1em;
      }
    `];
  }

  render() {
    this._adjustModalDialogHeight();

    if (!this.entity) {
      return html`<bn-loading></bn-loading>`;
    }

    return html`
      <div class="main-container">
        <p class="subdued">
          Select the organization you wish to assign the work order to.
        </p>
        <div class="destination-container">
          <div class="destination-header">
            <h4>Installer Organization:</h4>
            ${this._renderSearchBox()}
          </div>
        </div>
        ${this.loading ? html`<bn-loading></bn-loading>` : this._renderOrgBrowser()}
      </div>
     `;
  }

  _renderSearchBox() {
    return html`
      <div class="search-container">
        <form @submit="${this._submitSearch}">
          <bn-icon name="fal fa-search" class="search-icon"></bn-icon>
          <input
            type="search"
            placeholder="Search organizations"
            .value="${this.query}"
            @input="${this._handleSearchQueryChange}"
          />
        </form>
      </div>
    `;
  }

  _renderOrgBrowser() {
    if (this._searchQuery()) {
      return this._renderSearchResults();
    }

    if (!this.selectedOrganization) {
      return html``;
    }

    const crumbItems = [
      ...this.ancestorOrganizations.map((organization) => ({
        organization,
        onClick: () => this._selectOrganization(organization),
      })),
      { organization: this.selectedOrganization },
    ];

    return html`
      <bn-surface>
        <organization-bread-crumbs .organizationItems="${crumbItems}" explicitlinks>
          <organization-dropdown
            .organizations="${this.childOrganizations}"
            @organizationclicked="${(e) => this._selectOrganization(e.detail.organization)}"
          ></organization-dropdown>
        </organization-bread-crumbs>
      </bn-surface>
      <div>
        <bn-form-field
          title="Scheduled For"
          name="scheduled-for"
          type="date"
          @input="${(e) => this.scheduledFor = e.target.input.value}"
          .value="${this.scheduledFor}"
          required
        ></bn-form-field>
      </div>
      <bn-flash></bn-flash>
      <div class="actions well">
        <button class="button button--contained" @click="${this._submitAction}" ?disabled="${!this.scheduledFor}">Assign Work Order</button>
        <span class="action-text">
          to <span class="destination-name">${this.selectedOrganization.properties.name}</span>
        </span>
      </div>
    `;
  }

  _renderSearchResults() {
    if (this.searchResultOrganizations.length === 0) {
      return html`<p class="empty-search-results">No organizations found</p>`;
    }
    return html`
      <div class="search-results">
        ${this.searchResultOrganizations.map((organization) => html`
          <bn-surface class="search-result-item" @click="${() => this._selectOrganization(organization)}">
            <organization-ancestors-bread-crumbs
              .href="${organization.getLinkByRel('via').href}"
            ></organization-ancestors-bread-crumbs>
          </bn-surface>
        `)}
        <bn-load-more-button
          .href="${this.href}"
          @moreloaded="${this._onMoreLoaded}"
        ></bn-load-more-button>
      </div>
    `;
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    this.searchResultOrganizations = this.entity.getSubEntitiesByRel('item');
    this.selectedOrganization = this.entity.getSubEntityByRel('https://api.binsentry.com/rel/selected-organization');
    this.childOrganizations = this.entity.getSubEntitiesByRel('https://api.binsentry.com/rel/organization-child');
    this.ancestorOrganizations = this.entity.getSubEntitiesByRel('https://api.binsentry.com/rel/organization-ancestor');
  }

  _adjustModalDialogHeight() {
    const modal = document.querySelector('bn-modal#assign-installer-modal');
    if (this._searchQuery()) {
      modal.setAttribute('noscroll', true);
      modal.style.setProperty('--modal-min-height', '95%');
      return;
    }
    modal.removeAttribute('noscroll');
    modal.style.setProperty('--modal-min-height', '400px');
  }

  _handleSearchQueryChange(e) {
    this.query = e.target.value;
    // automatically switch back to browse when search box is cleared
    if (!this.query && this._searchQuery()) {
      this._submitSearch();
    }
  }

  _onMoreLoaded(e) {
    const { entity } = e.detail;
    this.searchResultOrganizations = [...this.searchResultOrganizations, ...entity.getSubEntitiesByRel('item')];
  }

  _searchQuery() {
    return new URL(this.href).searchParams.get('searchQuery') || '';
  }

  _selectOrganization(org) {
    this.query = '';
    this.href = org.getActionByName('select').href;
  }

  async _submitSearch(e) {
    e && e.preventDefault();
    const action = this.entity.getActionByName('search-organizations');

    const fields = this.getActionFields(action);
    fields.set('searchQuery', this.query);

    const url = new URL(action.href);
    url.search = fields;

    this.href = url.href;
  }

  async _submitAction() {
    const action = this.entity.getAction('assign-work-order');
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.clear();
    try {
      if (!action) {
        throw new Error(`Could not find [${this.actionname}] action`);
      }
      const fields = this.getActionFields(action);
      fields.set('scheduledFor', this.scheduledFor);
      this.dispatchEvent(new CustomEvent('submit', { bubbles: true, composed: true }));
      const { entity, response, error } = await this.submitAction(action, fields);
      if (error) {
        throw new Error(error);
      }
      flash && flash.success(this.successtext || 'Success.');
      this.dispatchEvent(new CustomEvent('response', { detail: { entity, response }, bubbles: true, composed: true }));
    } catch (error) {
      flash && flash.critical(html`${this.errortext}<div><code>${error.message}</code></div>`);
      this.dispatchEvent(new ErrorEvent('error', { error, bubbles: true, composed: true }));
    }
  }
}

window.customElements.define('assign-installer', AssignInstaller);
